const Wildcard = Symbol('*');
const typeMap = () => {
  const map = new Map();
  const count = type => {
    var _map$get$size, _map$get;
    return (_map$get$size = (_map$get = map.get(type)) === null || _map$get === void 0 ? void 0 : _map$get.size) !== null && _map$get$size !== void 0 ? _map$get$size : 0;
  };
  return {
    count,
    clear: () => map.clear(),
    get: type => map.get(type),
    add: (type, callback) => {
      if (!map.has(type)) {
        map.set(type, new Set());
      }
      map.get(type).add(callback);
    },
    remove: (type, callback) => {
      var _map$get2, _map$get3;
      (_map$get2 = map.get(type)) === null || _map$get2 === void 0 || _map$get2.delete(callback);
      if (((_map$get3 = map.get(type)) === null || _map$get3 === void 0 ? void 0 : _map$get3.size) === 0) {
        map.delete(type);
        return 0;
      }
      return count(type);
    }
  };
};
const recursionMessage = type => `Recursive emit for ${String(type)}`;
export const isRecursionError = error => error.message.match(recursionMessage('(.*)'));

/**
 * Create a typed event emitter.
 *
 * To type events, provide a type that maps event type name > event data.
 *
 * Set the data's type to `void` or `undefined` to define an empty event.
 *
 * Set the key to a string to allow arbitrary string types that map to a specific
 * event shape.
 *
 * @param opts - The options for the event emitter
 * @param opts.onEmitError - Optional callback for error handling
 * @returns The event emitter with methods:
 * - `.count(type)` to count event subscribers (does not count wildcard subscribers)
 * - `.on(type, callback: (data, off))` to subscribe to an event
 * - `.all({ type, data }, off)` to subscribe to all events (a "wildcard subscriber")
 * - `.emit(type, data)` to emit an event
 * - `.teardown()` to cleanup the emitter
 *
 * @example
 * ```typescript
 * // Simple event type
 * const emitter = create<{ 'test-type': string }>();
 *
 * emitter.on('test-type', (data) => {
 *   console.log(data);
 * });
 *
 * emitter.emit('test-type', 'test');
 *
 * // Arbitrary string type with known shape
 * const emitter = create<{ [key: string]: number }>();
 *
 * emitter.on('someKey', callback);
 *
 * emitter.emit('someKey', 123);
 *
 * // A mix of types
 * const emitter = create<{ 'test-type': string; [key: string]: number }>();
 *
 * emitter.on('test-type', (data) => {
 *   console.log(data);
 * });
 *
 * emitter.emit('test-type', 'test');
 * emitter.emit('arbitrary', 123);
 *
 * ```
 */
export const create = (opts = {}) => {
  const onEmitError = opts.onEmitError || (({
    error
  }) => !!isRecursionError(error));
  const handlers = typeMap();
  const off = (type, callback) => handlers.remove(type, callback);
  const emitSafe = (type, data, wildcard) => {
    const callbacks = handlers.get(wildcard ? Wildcard : type);
    if (!callbacks) {
      return;
    }
    for (const callback of callbacks) {
      try {
        if (wildcard) {
          callback({
            type,
            data
          }, () => off(Wildcard, callback));
        } else {
          callback(data, () => off(type, callback));
        }
      } catch (error) {
        if (onEmitError({
          type: type,
          data,
          error: error
        })) {
          throw error;
        }
      }
    }
  };
  const emitting = new Set();
  function emit(type, data) {
    if (emitting.has(type)) {
      throw Error(recursionMessage(type));
    }
    emitting.add(type);
    emitSafe(type, data);
    emitSafe(type, data, true);
    emitting.delete(type);
  }
  return {
    /**
     * Get the current subscriber count for a specific event.
     *
     * Does not count wildcard subscribers.
     *
     * @param type The event type to count
     * @returns The number of subscribers for this event
     *
     * @example
     * ```typescript
     * const off = emitter.on('event', callback);
     * emitter.count('event'); // 1
     * off();
     * emitter.count('event'); // 0
     *
     * ```
     */
    count: type => handlers.count(type),
    /**
     * Subscribe to an event.
     *
     * Returns an `off` callback to unsubscribe. `off`:
     * - returns the updated subscriber count
     * - is also provided as a second arg to the callback
     *
     * @param type The event type to subscribe to
     * @param callback The callback to call when the event is emitted
     * @returns A function to unsubscribe from the event
     *
     * @example
     * ```typescript
     * const off = emitter.on('event', console.log);
     *
     * emitter.emit('event', 'test'); // 'test'
     * off();
     * emitter.emit('event', 'test'); // nothing
     *
     * ```
     */
    on: (type, callback) => {
      handlers.add(type, callback);
      return () => off(type, callback);
    },
    /**
     * Subscribe to all events emitted through this emitter.
     *
     * Called with a { type, data } tuple that is typed as a discriminated
     * union of all possible event types.
     *
     * Returns an `off` callback to unsubscribe. `off`:
     * - does not return wildcard subscriber count
     * - is also provided as a second arg to the callback
     *
     * @example
     * ```typescript
     * const off = emitter.onAll(({ type, data }) => {
     *   console.log(type, data);
     * });
     *
     * ```
     */
    onAll: callback => {
      handlers.add(Wildcard, callback);
      return () => {
        off(Wildcard, callback);
      };
    },
    /**
     * Emit an event of the specified type.
     *
     * If the data is typed as void or undefined, will not accept a data param.
     *
     * Prevents recursive emissions by throwing immediately (cannot be stopped by the error callback)
     *
     * @param type The event type to emit
     * @param data The data to emit
     *
     * @example
     * ```typescript
     * emitter.emit('event', 'test');
     * ```
     */
    emit,
    /**
     * Cleanup the emitter.
     */
    teardown: () => {
      emitting.clear();
      handlers.clear();
    }
  };
};